/**
 * CameraPiPBlock — renders a camera_pip() call (without x/y) as an inline
 * 3D view inside the parameter panel.
 *
 * Reuses the same internals as PiPPanel (PiPCameraSetup, PiPModelRenderer,
 * SceneLighting, PostProcessing) without the drag / context-menu chrome.
 *
 * Geometry is parsed from BlobContext (which is populated by the viewer after
 * each compile). SceneSettings are read from their context so lighting and
 * material properties match the main viewer.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { PerspectiveCamera, OrthographicCamera } from '@react-three/drei';
import * as THREE from 'three';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';

import type { PipCameraConfig } from '@/utils/parameterUtils';
import type { ColoredGeometry } from '@/components/viewer/types';
import { useBlob } from '@/contexts/BlobContext';
import { useColor } from '@/contexts/ColorContext';
import { useSceneSettings } from '@/contexts/SceneSettingsContext';
import { SceneBackground } from '@/components/viewer/scene/SceneBackground';
import { SceneLighting } from '@/components/viewer/scene/SceneLighting';
import { PostProcessing } from '@/components/viewer/postprocessing/PostProcessing';
import { WireframeOverlay } from '@/components/viewer/model/WireframeOverlay';
import { createMaterial } from '@/components/viewer/materials/materialUtils';
import type { MaterialSettings } from '@/contexts/SceneSettingsContext';

// ---------------------------------------------------------------------------
// Grayscale helpers
// ---------------------------------------------------------------------------

/** ITU-R BT.709 luma — same coefficients as the main viewer's grayscale mode. */
function toLumaGray(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Converts a ColoredGeometry array to luma-grayscale by replacing each color
 * with its luminance value. Returns new objects (does not mutate).
 */
function toGrayscaleGeometries(cgs: ColoredGeometry[]): ColoredGeometry[] {
  return cgs.map((cg) => {
    const luma = toLumaGray(cg.color[0], cg.color[1], cg.color[2]);
    return { ...cg, color: [luma, luma, luma, cg.color[3]] };
  });
}

/** Matte material settings used for grayscale mode — strips all reflections. */
const GRAYSCALE_MATERIAL_OVERRIDES: Partial<MaterialSettings> = {
  metalness: 0,
  roughness: 1,
  envMapIntensity: 0,
};

// ---------------------------------------------------------------------------
// Helpers shared with PiPPanel (inlined to avoid exporting private internals)
// ---------------------------------------------------------------------------

function ColoredMeshItem({
  cg,
  materialSettings,
}: {
  cg: ColoredGeometry;
  materialSettings: MaterialSettings;
}) {
  const color = useMemo(
    () => new THREE.Color(cg.color[0], cg.color[1], cg.color[2]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cg.color[0], cg.color[1], cg.color[2]],
  );

  const material = useMemo(
    () =>
      createMaterial(color, materialSettings, {
        perObject: cg.material ?? undefined,
        isGhost: cg.isGhost,
        opacity: cg.isGhost ? 0.3 : cg.color[3],
        transparent: cg.color[3] < 1 || !!cg.isGhost,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [color, materialSettings, cg.material, cg.isGhost, cg.color[3]],
  );

  useEffect(() => () => material.dispose(), [material]);
  return <mesh geometry={cg.geometry} material={material} />;
}

function ModelRenderer({
  geometry,
  coloredGeometries,
  color,
  materialSettings,
}: {
  geometry?: THREE.BufferGeometry;
  coloredGeometries: ColoredGeometry[];
  color: string;
  materialSettings: MaterialSettings;
}) {
  if (coloredGeometries.length > 0) {
    return (
      <group rotation={[-Math.PI / 2, 0, 0]}>
        {coloredGeometries.map((cg) => (
          <ColoredMeshItem
            key={cg.geometry.uuid}
            cg={cg}
            materialSettings={materialSettings}
          />
        ))}
      </group>
    );
  }
  if (geometry) {
    return (
      <group rotation={[-Math.PI / 2, 0, 0]}>
        <mesh geometry={geometry}>
          <meshStandardMaterial
            color={color}
            roughness={materialSettings.roughness}
            metalness={materialSettings.metalness}
            envMapIntensity={materialSettings.envMapIntensity}
            flatShading={materialSettings.flatShading}
          />
        </mesh>
      </group>
    );
  }
  return null;
}

// Camera orientor: runs inside R3F Canvas to position the fixed camera.
function CameraOrientor({ config }: { config: PipCameraConfig }) {
  const { camera, invalidate } = useThree();

  useEffect(() => {
    camera.position.set(...config.position);
    if (config.type === 'orthographic') {
      (camera as THREE.OrthographicCamera).zoom = 500 / Math.max(config.fov, 1);
    } else {
      (camera as THREE.PerspectiveCamera).fov = config.fov;
    }
    camera.lookAt(config.target[0], config.target[1], config.target[2]);
    camera.updateProjectionMatrix();
    invalidate();
  }, [
    camera,
    invalidate,
    config.position[0], // eslint-disable-line react-hooks/exhaustive-deps
    config.position[1],
    config.position[2],
    config.target[0],
    config.target[1],
    config.target[2],
    config.fov,
    config.type,
  ]);

  return null;
}

function CameraSetup({ config }: { config: PipCameraConfig }) {
  if (config.type === 'orthographic') {
    const zoom = 500 / Math.max(config.fov, 1);
    return (
      <>
        <OrthographicCamera
          makeDefault
          position={config.position}
          zoom={zoom}
          near={0.01}
          far={5000}
        />
        <CameraOrientor config={config} />
      </>
    );
  }
  return (
    <>
      <PerspectiveCamera
        makeDefault
        position={config.position}
        fov={config.fov}
        near={0.1}
        far={5000}
      />
      <CameraOrientor config={config} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Geometry lifecycle — parse blobs to THREE.BufferGeometry
// ---------------------------------------------------------------------------

interface GeometryState {
  geometry: THREE.BufferGeometry | null;
  coloredGeometries: ColoredGeometry[];
}

function useLocalGeometry(
  blob: Blob | null,
  coloredBlobs: {
    color: [number, number, number, number];
    blob: Blob;
    isGhost?: boolean;
  }[],
): GeometryState {
  const [state, setState] = useState<GeometryState>({
    geometry: null,
    coloredGeometries: [],
  });
  const genRef = useRef(0);

  useEffect(() => {
    const gen = ++genRef.current;
    const loader = new STLLoader();

    if (coloredBlobs.length > 0) {
      Promise.all(
        coloredBlobs.map(async (cb) => {
          const buf = await cb.blob.arrayBuffer();
          const geom = loader.parse(buf);
          geom.computeVertexNormals();
          return {
            geometry: geom,
            color: cb.color,
            isGhost: cb.isGhost,
          } as ColoredGeometry;
        }),
      ).then((geoms) => {
        if (gen !== genRef.current) {
          geoms.forEach((g) => g.geometry.dispose());
          return;
        }
        setState({ geometry: null, coloredGeometries: geoms });
      });
    } else if (blob) {
      blob.arrayBuffer().then((buf) => {
        if (gen !== genRef.current) return;
        const geom = loader.parse(buf);
        geom.computeVertexNormals();
        setState({ geometry: geom, coloredGeometries: [] });
      });
    } else {
      setState({ geometry: null, coloredGeometries: [] });
    }

    return () => {
      // Cancel in-flight work by bumping gen
    };
  }, [blob, coloredBlobs]);

  // Dispose geometries on unmount
  useEffect(() => {
    return () => {
      state.geometry?.dispose();
      state.coloredGeometries.forEach((cg) => cg.geometry.dispose());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}

// ---------------------------------------------------------------------------
// Parse aspect ratio string "W:H" → padding-bottom percentage
// ---------------------------------------------------------------------------

function arToPaddingBottom(ar: string): string {
  const parts = ar.split(':').map(Number);
  if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) {
    return `${(parts[1] / parts[0]) * 100}%`;
  }
  return `${(3 / 4) * 100}%`; // default 4:3
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export function CameraPiPBlock({ config }: { config: PipCameraConfig }) {
  const { blob, coloredBlobs } = useBlob();
  const { color } = useColor();
  const { mergedSettings, sceneOverrides, sceneLights } = useSceneSettings();
  const lights = sceneLights;

  const { geometry, coloredGeometries } = useLocalGeometry(blob, coloredBlobs);

  // Use the scene override background color if set, otherwise fall back to
  // the same default the main viewer uses ('#6b6b6d').
  const backgroundColor = sceneOverrides.backgroundColor ?? '#6b6b6d';

  const paddingBottom = arToPaddingBottom(config.ar);
  const mode = config.mode ?? 'default';
  const isWireframe = mode === 'wireframe';
  const isGrayscale = mode === 'grayscale';

  // Grayscale: luma-converted geometries + matte material overrides.
  const renderGeometries = isGrayscale
    ? toGrayscaleGeometries(coloredGeometries)
    : coloredGeometries;
  const renderColor = isGrayscale
    ? (() => {
        const c = new THREE.Color(color);
        const l = toLumaGray(c.r, c.g, c.b);
        return `rgb(${Math.round(l * 255)},${Math.round(l * 255)},${Math.round(l * 255)})`;
      })()
    : color;
  const sceneSettings = isGrayscale
    ? {
        ...mergedSettings,
        material: {
          ...mergedSettings.material,
          ...GRAYSCALE_MATERIAL_OVERRIDES,
        },
      }
    : mergedSettings;

  // Wireframe: suppress all post-processing; grayscale: suppress bloom/SSAO.
  const postSettings = isWireframe
    ? null
    : {
        ...sceneSettings,
        ssao: { ...sceneSettings.ssao, enabled: false },
        ...(isGrayscale && {
          bloom: { ...sceneSettings.bloom, enabled: false },
        }),
      };

  return (
    <div className="w-full overflow-hidden rounded-md border border-border bg-card shadow-sm">
      {/* Optional title — shown above the canvas */}
      {config.title && (
        <div className="border-b border-border px-2.5 py-1.5">
          <span className="text-xs font-medium text-foreground">
            {config.title}
          </span>
        </div>
      )}

      {/* Aspect-ratio canvas box */}
      <div style={{ position: 'relative', width: '100%', paddingBottom }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          <Canvas
            frameloop="demand"
            linear
            dpr={[1, 2]}
            gl={{ antialias: true }}
          >
            <CameraSetup config={config} />
            <SceneBackground color={backgroundColor} />
            <SceneLighting
              sceneSettings={sceneSettings}
              showMovingLight={false}
              showGrid={false}
              wireframeOpacity={isWireframe ? 1 : 0}
              lights={lights}
              skipEnvironment={true}
            />
            {!isWireframe && (
              <ModelRenderer
                geometry={geometry ?? undefined}
                coloredGeometries={renderGeometries}
                color={renderColor}
                materialSettings={sceneSettings.material}
              />
            )}
            {isWireframe && (
              <WireframeOverlay
                geometry={geometry ?? undefined}
                coloredGeometries={coloredGeometries}
                wireframeOpacity={1}
              />
            )}
            {postSettings && (
              <PostProcessing sceneSettings={postSettings} multisampling={4} />
            )}
          </Canvas>
        </div>

        {/* Optional in-canvas label overlay (bottom-left) */}
        {config.label && (
          <div className="absolute bottom-0 left-0 right-0 truncate bg-black/40 px-2 py-0.5 text-xs text-white/80">
            {config.label}
          </div>
        )}
      </div>

      {/* Optional caption — shown below the canvas */}
      {config.caption && (
        <div className="border-t border-border px-2.5 py-1.5">
          <span className="text-xs text-muted-foreground">
            {config.caption}
          </span>
        </div>
      )}
    </div>
  );
}
